import { Typography } from 'Components/atoms';
import { ACTION_TABLE_HEADERS } from 'Core-utils/constants/constants';
import { Button, Image } from 'react-bootstrap';
import Delete from 'Assets/icons/delete.svg';
import { IoLockOpenOutline } from '@react-icons/all-files/io5/IoLockOpenOutline';
import { IoLockClosedOutline } from '@react-icons/all-files/io5/IoLockClosedOutline';
import { CustomTooltip } from 'Components/atoms';
import { IoMdInformationCircleOutline } from '@react-icons/all-files/io/IoMdInformationCircleOutline';
import { SensitiveFieldState } from '../CustomActionWizard';
import classNames from 'classnames';
import '../styles.scss';
import { useI18n } from 'Hooks/useI18n';

interface TableFormProps {
    label: string;
    headers: string[];
    data: Record<string, string>;
    dataKey: 'headers' | 'parameters';
    handleAdd: () => void;
    handleDelete: (index: number) => () => void;
    handleChange: (index: number, field: string) => (value: React.ChangeEvent<HTMLTextAreaElement>) => void;
    renderInputField: (
        value: string,
        onChange: (newValue: React.ChangeEvent<HTMLTextAreaElement>) => void,
        isTableRow: boolean,
        isDisabled?: boolean,
    ) => React.ReactNode;
    buttonLabel: string;
    tooltipLabel: string;
    sensitiveState: SensitiveFieldState;
    handleToggleSensitive: (dataKey: 'headers' | 'parameters', fieldKey: string) => void;
}

const TableForm = ({
    label,
    data,
    dataKey,
    handleAdd,
    handleDelete,
    handleChange,
    renderInputField,
    buttonLabel,
    tooltipLabel,
    sensitiveState,
    handleToggleSensitive,
}: TableFormProps) => {
    const { t } = useI18n();

    // Get the appropriate sensitive arrays based on dataKey
    const sensitiveArray = dataKey === 'parameters' ? sensitiveState.sensitiveParams : sensitiveState.sensitiveHeaders;
    const maskedFromApi = dataKey === 'parameters' ? sensitiveState.maskedParamsFromApi : sensitiveState.maskedHeadersFromApi;

    return (
        <div className="action-block__action-table d-flex flex-column gap-2 align-items-start">
            <div className="d-flex gap-1">
                <Typography variant="body3" color="text-medium-emphasis">
                    {label}
                </Typography>
                <CustomTooltip label={tooltipLabel}>
                    <IoMdInformationCircleOutline className="text-text-low-emphasis" />
                </CustomTooltip>
            </div>
            <table className="table table-striped table-bordered action-block____custom-table">
                <thead>
                    <tr>
                        <th>
                            <Typography variant="body2" color="text-high-emphasis">
                                {ACTION_TABLE_HEADERS[0]}
                            </Typography>
                        </th>
                        <th colSpan={3}>
                            <Typography variant="body2" color="text-high-emphasis">
                                {ACTION_TABLE_HEADERS[1]}
                            </Typography>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {data &&
                        Object.entries(data).map(([key, value], index) => {
                            const isSensitive = sensitiveArray.includes(key);
                            const isMaskedFromApi = maskedFromApi.has(key);

                            // Determine if the value field should be disabled
                            // Masked from API fields are read-only (must delete & re-add to change)
                            const isValueDisabled = isMaskedFromApi;

                            // Get tooltip text for sensitive icon
                            const sensitiveTooltip = isMaskedFromApi
                                ? t('CUSTOM_ACTION_UNEDITABLE_VALUE_HELP')
                                : isSensitive
                                  ? t('CUSTOM_ACTION_VALUE_MASKED_HELP')
                                  : t('CUSTOM_ACTION_MASK_VALUE_HELP');

                            return (
                                <tr key={index}>
                                    <td>{renderInputField(key, handleChange(index, 'key'), true, isValueDisabled)}</td>
                                    <td>{renderInputField(value, handleChange(index, 'value'), true, isValueDisabled)}</td>
                                    <td className="action-block__text-center d-flex align-items-center flex-row justify-content-center gap-4">
                                        <CustomTooltip label={sensitiveTooltip}>
                                            {isSensitive ? (
                                                <IoLockClosedOutline
                                                    className={classNames('action-block__icon', {
                                                        'action-block__icon--sensitive': true,
                                                        'action-block__icon--locked': isMaskedFromApi,
                                                    })}
                                                    onClick={() => !isMaskedFromApi && handleToggleSensitive(dataKey, key)}
                                                    data-testid="sensitive-row"
                                                />
                                            ) : (
                                                <IoLockOpenOutline
                                                    className="action-block__icon"
                                                    onClick={() => handleToggleSensitive(dataKey, key)}
                                                    data-testid="sensitive-row"
                                                />
                                            )}
                                        </CustomTooltip>
                                        <Image
                                            src={Delete}
                                            className="action-block__icon"
                                            onClick={handleDelete(index)}
                                            data-testid="delete-row"
                                        />
                                    </td>
                                </tr>
                            );
                        })}
                </tbody>
            </table>
            <Button className="body5 text-primary-500 text-uppercase border-0 p-0" onClick={handleAdd}>
                {buttonLabel}
            </Button>
        </div>
    );
};

export default TableForm;
